import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { AudioSession, type AudioInterruption, type AudioSessionMode } from './audio-session';

/**
 * The one place that knows whether this is a browser or a Capacitor shell
 * (plan doc 17).
 *
 * Feature code calls these and never `Capacitor.*` directly, so the web
 * fallback for every capability lives next to its native version and the
 * behavior on a phone can be read from one file. Plugin packages are imported
 * lazily inside the native branches, which keeps them out of the web bundle.
 */

export type PlatformName = 'ios' | 'android' | 'web';
export type Unsubscribe = () => void;

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function platformName(): PlatformName {
  const name = Capacitor.getPlatform();
  return name === 'ios' || name === 'android' ? name : 'web';
}

/**
 * Subscribes through a plugin whose listener handle arrives asynchronously,
 * and copes with being unsubscribed before it does.
 */
function subscribe(attach: () => Promise<PluginListenerHandle>): Unsubscribe {
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  void attach()
    .then((h) => {
      if (cancelled) void h.remove();
      else handle = h;
    })
    .catch(() => {
      // A plugin missing from this build is a capability the app lives without.
    });
  return () => {
    cancelled = true;
    void handle?.remove();
  };
}

/* ------------------------------------------------------------ app state */

/**
 * Foreground and background. Native reports it through the App plugin; the
 * web has `visibilitychange`, which is the same thing for our purposes.
 */
export function onAppStateChange(listener: (active: boolean) => void): Unsubscribe {
  if (isNative()) {
    return subscribe(async () => {
      const { App } = await import('@capacitor/app');
      return App.addListener('appStateChange', ({ isActive }) => listener(isActive));
    });
  }
  if (typeof document === 'undefined') return () => {};
  const onVisible = (): void => listener(document.visibilityState === 'visible');
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}

/**
 * Android's back button: go back in history, or leave the app from the root.
 * Without this, back closes the app from every screen — Capacitor's default
 * is to do nothing at all when the web view cannot go back.
 */
export function installBackButton(): Unsubscribe {
  if (!isNative()) return () => {};
  return subscribe(async () => {
    const { App } = await import('@capacitor/app');
    return App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else void App.minimizeApp();
    });
  });
}

/* ----------------------------------------------------------- deep links */

/**
 * The in-app path for a universal link or a custom-scheme URL, or null when
 * the URL is not one of ours. Pure, so the routing rule is testable.
 */
export function pathFromAppUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  // Custom scheme (`audigoal://songs/x`) puts the first segment in the host.
  const path =
    url.protocol === 'audigoal:'
      ? `/${url.host}${url.pathname === '/' ? '' : url.pathname}`
      : url.pathname;
  if (!/^\/(songs|u|lesson|practice|profile|certificate)(\/|$)/.test(path)) return null;
  return `${path}${url.search}`;
}

/** Deep links while running, and the one the app was launched with. */
export function onAppUrlOpen(listener: (path: string) => void): Unsubscribe {
  if (!isNative()) return () => {};
  const deliver = (raw: string | undefined): void => {
    if (raw === undefined) return;
    const path = pathFromAppUrl(raw);
    if (path !== null) listener(path);
  };
  void import('@capacitor/app')
    .then(({ App }) => App.getLaunchUrl())
    .then((launch) => deliver(launch?.url))
    .catch(() => {});
  return subscribe(async () => {
    const { App } = await import('@capacitor/app');
    return App.addListener('appUrlOpen', ({ url }) => deliver(url));
  });
}

/* ---------------------------------------------------------------- share */

export type ShareOutcome = 'shared' | 'cancelled' | 'unsupported';

/**
 * The system share sheet for text and links. Files are shared by the caller
 * through the Web Share API, which the native web views also implement; this
 * is for the cases with no file, where the native sheet is the better one.
 */
export async function shareText(options: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<ShareOutcome> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({
        ...(options.title !== undefined ? { title: options.title } : {}),
        ...(options.text !== undefined ? { text: options.text } : {}),
        ...(options.url !== undefined ? { url: options.url } : {}),
      });
      return 'shared';
    } catch {
      return 'cancelled';
    }
  }
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'unsupported';
  }
  try {
    await navigator.share(options);
    return 'shared';
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    return 'unsupported';
  }
}

/* ----------------------------------------------------------- appearance */

/**
 * The status bar's text color follows the theme. Nothing to do on the web,
 * where `theme-color` in the document does the same job.
 */
export async function applySystemBars(theme: 'light' | 'dark'): Promise<void> {
  if (!isNative()) return;
  try {
    const { SystemBars, SystemBarsStyle } = await import('@capacitor/core');
    await SystemBars.setStyle({
      style: theme === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
    });
  } catch {
    // Older shells without SystemBars keep the platform default.
  }
}

/** Hides the native splash once the first real screen has painted. */
export async function hideSplash(): Promise<void> {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    // launchAutoHide is off, so a failure here would leave the splash up;
    // the plugin is a core dependency of the native project and is present.
  }
}

/* ---------------------------------------------------------------- audio */

/**
 * Configures the native session before the microphone starts, and back to
 * playback when it stops. A no-op on the web.
 */
export async function configureAudio(mode: AudioSessionMode): Promise<void> {
  if (!isNative()) return;
  try {
    await AudioSession.configure({ mode });
  } catch {
    // The plugin is part of the native project; if it is missing the browser
    // defaults still play, only quieter.
  }
}

/** A phone call, or Siri, taking the audio hardware away and giving it back. */
export function onAudioInterruption(
  listener: (interruption: AudioInterruption) => void,
): Unsubscribe {
  if (!isNative()) return () => {};
  return subscribe(() => AudioSession.addListener('audioInterrupted', listener));
}
